// Eskom PARCC — one transaction's audit trail, as a collapsible section inside the
// read-only TransactionDetail (a flat Card, no tab machinery). One time-ordered
// timeline merging AuditLog entries and CES snapshots: when · who · action · surface
// · what — where *what* renders the from→to field changes, the same idiom as the
// bulk-update preview. A row backed by a CES snapshot opens a flyout that fetches the
// captured-sheet blobs ON OPEN (never on list render) and diffs it against its
// predecessor. Visible to everyone who can see the transaction (the fetch hook is
// role-scoped: admins go direct, others through the server-scoped proxy).
import { useMemo, useState } from 'react';
import { Flyout, LoadingState, ErrorState } from '../../components/ui';
import { useTransactionHistory, useEvaluationSnapshotDetail } from '../../hooks';
import { mergeHistory, type HistoryRow } from './mergeHistory';
import { diffCesSnapshot } from './diffCesSnapshot';
import { diffFields, TRANSACTION_FIELD_LABELS, type FieldChange } from '../../lib/fieldDiff';
import { actionLabel, surfaceLabel } from './auditActions';
import type { SnapshotDetail } from '../../lib/db';

function formatAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-ZA');
}

/** The from→to changes, rendered like the bulk preview (`Label: old → new`). */
function Changes({ changes }: { changes: readonly FieldChange[] }): React.JSX.Element {
  return (
    <ul className="audit-changes">
      {changes.map((c) => (
        <li key={c.field}>
          {c.label}: <em>{c.from}</em> → <strong>{c.to}</strong>
        </li>
      ))}
    </ul>
  );
}

/** One timeline row. */
function Row({
  row,
  onOpenSnapshot,
}: {
  row: HistoryRow;
  onOpenSnapshot: (s: { id: string; previousId: string | null }) => void;
}): React.JSX.Element {
  return (
    <div className="audit-row">
      <div className="small audit-when">
        {formatAt(row.at)} · {row.actorEmail ?? '—'}
      </div>
      <div className="audit-what">
        <strong>{actionLabel(row.action)}</strong> <span className="anchor">{surfaceLabel(row.action)}</span>
        {row.changes.length > 0 ? (
          <Changes changes={row.changes} />
        ) : row.note ? (
          <div className="small">{row.note}</div>
        ) : null}
        {row.snapshot && (
          <button
            type="button"
            className="btn sm"
            style={{ marginTop: 6 }}
            onClick={() => onOpenSnapshot({ id: row.snapshot!.id, previousId: row.snapshot!.previousId })}
          >
            View captured sheet
          </button>
        )}
      </div>
    </div>
  );
}

/** The captured-sheet flyout: fetches blobs on open, diffs against the predecessor. */
function SnapshotFlyout({
  snap,
  onClose,
}: {
  snap: { id: string; previousId: string | null } | null;
  onClose: () => void;
}): React.JSX.Element {
  const [tab, setTab] = useState<'changes' | 'snapshot'>('changes');
  const ids = snap ? [snap.id, snap.previousId].filter((v): v is string => v !== null) : [];
  const detailQuery = useEvaluationSnapshotDetail(ids, { enabled: snap !== null });

  const detail = detailQuery.data ?? [];
  const next = snap ? detail.find((d) => d.id === snap.id) ?? null : null;
  const prev = snap?.previousId ? detail.find((d) => d.id === snap.previousId) ?? null : null;

  const fieldChanges = useMemo(
    () =>
      next && prev
        ? diffFields(
            (prev.workingPaper ?? {}) as Record<string, unknown>,
            (next.workingPaper ?? {}) as Record<string, unknown>,
            TRANSACTION_FIELD_LABELS,
          )
        : [],
    [next, prev],
  );
  const cesDiff = useMemo(
    () => (next && prev ? diffCesSnapshot(prev.fullEvaluation, next.fullEvaluation) : null),
    [next, prev],
  );

  return (
    <Flyout
      open={snap !== null}
      onClose={onClose}
      title="Captured evaluation sheet"
      subtitle={next ? formatAt(next.createdAt) : undefined}
      width="wide"
    >
      <div className="tabbar" style={{ marginBottom: 12 }}>
        <button type="button" className={tab === 'changes' ? 'tab active' : 'tab'} onClick={() => setTab('changes')}>
          Changes
        </button>
        <button type="button" className={tab === 'snapshot' ? 'tab active' : 'tab'} onClick={() => setTab('snapshot')}>
          Snapshot
        </button>
      </div>

      {detailQuery.isLoading && <LoadingState message="Loading captured sheet…" />}
      {detailQuery.error && (
        <ErrorState
          message="Could not load the captured sheet."
          detail={detailQuery.error instanceof Error ? detailQuery.error.message : String(detailQuery.error)}
          onRetry={() => void detailQuery.refetch()}
        />
      )}

      {!detailQuery.isLoading && !detailQuery.error && tab === 'changes' && (
        <ChangesTab hasPrev={snap?.previousId != null} fieldChanges={fieldChanges} cesDiff={cesDiff} />
      )}
      {!detailQuery.isLoading && !detailQuery.error && tab === 'snapshot' && <SnapshotTab detail={next} />}
    </Flyout>
  );
}

function ChangesTab({
  hasPrev,
  fieldChanges,
  cesDiff,
}: {
  hasPrev: boolean;
  fieldChanges: FieldChange[];
  cesDiff: ReturnType<typeof diffCesSnapshot> | null;
}): React.JSX.Element {
  if (!hasPrev) {
    return <div className="empty">First captured sheet — nothing to compare against.</div>;
  }
  const nothing =
    fieldChanges.length === 0 &&
    cesDiff !== null &&
    cesDiff.added.length === 0 &&
    cesDiff.removed.length === 0 &&
    cesDiff.changed.length === 0 &&
    cesDiff.findingsDelta === 0;
  if (nothing) return <div className="empty">No changes from the previous captured sheet.</div>;
  return (
    <div className="audit-diff">
      {fieldChanges.length > 0 && (
        <section>
          <div className="k">Review fields</div>
          <Changes changes={fieldChanges} />
        </section>
      )}
      {cesDiff && cesDiff.changed.length > 0 && (
        <section>
          <div className="k">Criteria changed</div>
          <ul className="audit-changes">
            {cesDiff.changed.map((c) => (
              <li key={c.criterion}>
                {c.criterion}: <em>{c.from}</em> → <strong>{c.to}</strong>
              </li>
            ))}
          </ul>
        </section>
      )}
      {cesDiff && cesDiff.added.length > 0 && (
        <section>
          <div className="k">Criteria added</div>
          <div className="small">{cesDiff.added.join(', ')}</div>
        </section>
      )}
      {cesDiff && cesDiff.removed.length > 0 && (
        <section>
          <div className="k">Criteria removed</div>
          <div className="small">{cesDiff.removed.join(', ')}</div>
        </section>
      )}
      {cesDiff && cesDiff.findingsDelta !== 0 && (
        <section>
          <div className="k">Findings</div>
          <div className="small">
            {cesDiff.findingsDelta > 0 ? '+' : ''}
            {cesDiff.findingsDelta} finding(s)
          </div>
        </section>
      )}
    </div>
  );
}

function SnapshotTab({ detail }: { detail: SnapshotDetail | null }): React.JSX.Element {
  if (!detail) return <div className="empty">Snapshot unavailable.</div>;
  const rows =
    detail.fullEvaluation &&
    typeof detail.fullEvaluation === 'object' &&
    Array.isArray((detail.fullEvaluation as { cesRows?: unknown }).cesRows)
      ? ((detail.fullEvaluation as { cesRows: { seq?: number; criterion?: string; status?: string }[] }).cesRows)
      : [];
  return (
    <div>
      {rows.length === 0 ? (
        <div className="empty">This snapshot captured no CES rows.</div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Criterion</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.seq ?? i}>
                  <td>{r.seq ?? '—'}</td>
                  <td>{r.criterion ?? '—'}</td>
                  <td>{r.status?.trim() || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <details style={{ marginTop: 12 }}>
        <summary className="small">Raw JSON</summary>
        <pre className="audit-json">{JSON.stringify(detail.fullEvaluation ?? null, null, 2)}</pre>
      </details>
    </div>
  );
}

function HistoryTimeline({ referenceNo }: { referenceNo: string }): React.JSX.Element {
  const query = useTransactionHistory(referenceNo);
  const [openSnap, setOpenSnap] = useState<{ id: string; previousId: string | null } | null>(null);
  const rows = useMemo(
    () => (query.data ? mergeHistory(query.data.entries, query.data.snapshots) : []),
    [query.data],
  );

  if (query.isLoading) return <LoadingState message="Loading history…" />;
  if (query.error) {
    return (
      <ErrorState
        message="Could not load this transaction's history."
        detail={query.error instanceof Error ? query.error.message : String(query.error)}
        onRetry={() => void query.refetch()}
      />
    );
  }

  return (
    <div className="audit-timeline">
      {rows.length === 0 ? (
        <div className="empty">No activity recorded for this transaction yet.</div>
      ) : (
        rows.map((row) => <Row key={row.id} row={row} onOpenSnapshot={setOpenSnap} />)
      )}
      <p className="small" style={{ marginTop: 10 }}>
        Entries recorded before this release show who, when and which surface — but not the
        field values, which are captured from this release onward.
      </p>
      <SnapshotFlyout snap={openSnap} onClose={() => setOpenSnap(null)} />
    </div>
  );
}

/** The collapsible "History" section mounted inside TransactionDetail. Lazy: the
 *  timeline (and its fetch) mounts only when the section is expanded. */
export function TransactionHistory({ referenceNo }: { referenceNo: string }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="audit-section" style={{ marginTop: 16 }}>
      <button
        type="button"
        className="audit-section-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ fontWeight: 700 }}>History</span>
        <span className="small" aria-hidden>
          {open ? '▾ Hide' : '▸ Show'}
        </span>
      </button>
      {open && <HistoryTimeline referenceNo={referenceNo} />}
    </div>
  );
}
